import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { ContractedKmAlertDashboardDto } from 'api/dashboard/dtos/ContractedKmAlertDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ContractedKmAlerts.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const formatKm = (value: number) => {
	return Math.round(value).toLocaleString('fr-FR');
};

type Props = {
	// Legacy props (direct data passing)
	data?: ContractedKmAlertDashboardDto[] | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

const ContractedKmAlerts = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<ContractedKmAlertDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);

	const isLegacyMode = props.data !== undefined;
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			try {
				setFetchLoading(true);
				const criteria = props.filters
					? { ...props.filters }
					: { ...vehicleScope };
				const result = await DashboardService.getContractedKmProgress(criteria);
				if (!cancelled) setFetchedData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setFetchLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data || [], PAGE_SIZE);

	const getTooltipLabel = (item: ContractedKmAlertDashboardDto) => {
		return [item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || '';
	};

	const getKmTooltip = (item: ContractedKmAlertDashboardDto) => {
		const currentLabel = t('dashboard.columns.current_km' as any) as string;
		const contractedLabel = t('dashboard.columns.contracted_km' as any) as string;
		return `${currentLabel}: ${formatKm(item.currentKilometers)}<br />${contractedLabel}: ${formatKm(item.contractedKilometers)}`;
	};

	return (
		<Box className={tableStyles.container} title={t('dashboard.contracted_km_vs_actual' as any) as string} subTitleTooltip={t((scopeVehicleId ? 'dashboard.contracted_km_vs_actual_asset_tooltip' : 'dashboard.contracted_km_vs_actual_tooltip') as any) as string}>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
				<div className={styles.progressList}>
					{visibleItems.map((item, index) => {
						const percent = Math.round(item.percentageUsed);
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.progressRow}
								onClick={() => window.open(`/vehicles/details/${item.vehicleId}/contract/details/use/details/${item.contractId}`, '_blank')}
							>
								<div className={styles.progressLabel}>
									<span data-tip={getTooltipLabel(item) || undefined} data-for="contracted-km-alerts-tooltip" data-place="right">{item.registrationNumber}</span>
								</div>
								<div className={styles.progressTrack}>
									<div
										className={styles.progressFill}
										style={{ width: `${Math.min(percent, 100)}%`, background: color }}
									/>
								</div>
								<div className={styles.progressPercent} style={{ color }}>
									{percent}%
								</div>
								<div className={styles.progressKm} data-tip={getKmTooltip(item)} data-for="contracted-km-alerts-tooltip">
									{formatKm(item.currentKilometers)} / {formatKm(item.contractedKilometers)} km
								</div>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
				</div>
				<ReactTooltip id="contracted-km-alerts-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default ContractedKmAlerts;
